// Copilot FAB widget — mounts in admin layout only (SPEC 001 T149)
import React, { useState } from "react";
import { CopilotTurnResponse } from "../../interfaces/Copilot";
import CopilotChatPanel from "./CopilotChatPanel";
import CopilotConfirmModal from "./CopilotConfirmModal";
import CopilotOnboardingBubble from "./CopilotOnboardingBubble";
import { useCopilotOnboarding } from "./hooks/useCopilotOnboarding";
import { useCopilotSession } from "./hooks/useCopilotSession";
import { MdAutoAwesome, MdClose } from "react-icons/md";

const CopilotWidget: React.FC = () => {
  const [open, setOpen] = useState(false);
  const { showBubble, dismiss: dismissOnboarding } = useCopilotOnboarding();
  const {
    messages,
    sending,
    quotaRemaining,
    pendingToolCalls,
    send,
    editAndResend,
    newConversation,
    clearConversation,
    setPendingToolCalls,
    appendMessages,
  } = useCopilotSession();

  const handleClear = () => {
    if (messages.length === 0) return;
    if (window.confirm("Limpar esta conversa? As mensagens serão apagadas.")) {
      void clearConversation();
    }
  };

  const handleOpen = () => {
    setOpen(true);
    if (showBubble) dismissOnboarding();
  };

  const handleClose = () => setOpen(false);

  const handleConfirmed = (turn: CopilotTurnResponse) => {
    appendMessages(turn.newMessages);
    setPendingToolCalls(turn.pendingToolCalls);
  };

  const handleCancelled = (turn: CopilotTurnResponse) => {
    appendMessages(turn.newMessages);
    setPendingToolCalls([]);
  };

  return (
    <>
      {/* FAB button — fixed bottom-right above app shell */}
      <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end">
        {/* Onboarding bubble */}
        {showBubble && !open && (
          <CopilotOnboardingBubble onDismiss={dismissOnboarding} />
        )}

        {/* Chat panel drawer */}
        {open && (
          <div className="dv-ring-gradient mb-3 flex h-[480px] w-80 flex-col overflow-hidden rounded-2xl bg-white nina-surface shadow-dv-lg dark:bg-navy-800">
            {/* Header — identidade NINA (gradiente-assinatura animado) */}
            <div className="dv-animated-gradient flex items-center justify-between rounded-t-2xl bg-[length:200%_200%] px-3.5 py-2.5">
              <span className="flex items-center gap-2 text-sm font-bold text-white">
                <MdAutoAwesome className="h-4 w-4" aria-hidden="true" />
                NINA
                <span className="text-[11px] font-medium text-white/75">
                  · copiloto de IA
                </span>
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={newConversation}
                  className="rounded-md px-1.5 py-0.5 text-xs font-medium text-white/85 transition-colors hover:bg-white/15 hover:text-white"
                  aria-label="Nova conversa"
                  title="Nova conversa"
                >
                  Nova
                </button>
                <button
                  onClick={handleClear}
                  disabled={messages.length === 0}
                  className="rounded-md px-1.5 py-0.5 text-xs font-medium text-white/85 transition-colors hover:bg-white/15 hover:text-white disabled:opacity-40"
                  aria-label="Limpar conversa"
                  title="Limpar conversa"
                >
                  Limpar
                </button>
                <button
                  onClick={handleClose}
                  className="flex h-6 w-6 items-center justify-center rounded-md text-white/85 transition-colors hover:bg-white/15 hover:text-white"
                  aria-label="Fechar"
                >
                  <MdClose className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </div>

            {/* Chat body */}
            <div className="flex-1 overflow-hidden">
              <CopilotChatPanel
                messages={messages}
                sending={sending}
                quotaRemaining={quotaRemaining}
                onSend={send}
                onEdit={editAndResend}
              />
            </div>
          </div>
        )}

        {/* FAB — orbe NINA com o gradiente-assinatura animado */}
        <button
          onClick={open ? handleClose : handleOpen}
          className="dv-sheen dv-animated-gradient flex h-14 w-14 items-center justify-center rounded-full bg-[length:200%_200%] text-white shadow-dv-glow transition-transform hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-dv-cyan-500/50 focus:ring-offset-2 dark:focus:ring-offset-navy-900"
          aria-label={open ? "Fechar NINA, copiloto de IA" : "Abrir NINA, copiloto de IA"}
        >
          {open ? (
            <MdClose className="h-6 w-6" aria-hidden="true" />
          ) : (
            <MdAutoAwesome className="h-6 w-6" aria-hidden="true" />
          )}
        </button>
      </div>

      {/* Confirm modal — rendered outside FAB container to avoid z-index issues */}
      {pendingToolCalls.length > 0 && (
        <CopilotConfirmModal
          toolCalls={pendingToolCalls}
          onConfirmed={handleConfirmed}
          onCancelled={handleCancelled}
          onClose={() => setPendingToolCalls([])}
        />
      )}
    </>
  );
};

export default CopilotWidget;
